import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ScrollText } from 'lucide-react';
import client from '@/lib/api';
import PageHeader from '@/components/PageHeader';
import { TableSkeleton } from '@/components/Skeletons';
import Pagination from '@/components/PaginationBar';
import EmptyState from '@/components/EmptyState';
import { formatDateTime } from '@/lib/format';
import { Input } from '@/components/ui/input';

export default function AuditLogs() {
  const [page, setPage] = useState(1);
  const [entity, setEntity] = useState('');
  const q = useQuery({
    queryKey: ['audit-logs', { page, entity }],
    queryFn: () => client.get('/audit-logs', { params: { page, limit: 30, entity: entity || undefined } }).then((r) => r.data),
  });

  const items = q.data?.data || [];
  const meta = q.data?.meta?.pagination || { page: 1, totalPages: 1 };

  return (
    <div data-testid="audit-logs-page">
      <PageHeader title="Audit logs" description="Every non-GET action on the platform, in chronological order." />
      <div className="flex items-center gap-3 mb-4">
        <Input value={entity} onChange={(e) => { setEntity(e.target.value); setPage(1); }} placeholder="Filter by entity (e.g. warehouses, customers)" className="max-w-sm h-10 border-zinc-200 font-mono" data-testid="audit-entity-filter" />
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        {q.isLoading ? <TableSkeleton cols={6} /> : items.length === 0 ? (
          <EmptyState icon={ScrollText} title="No audit logs" />
        ) : (
          <table className="w-full text-sm" data-testid="audit-table">
            <thead>
              <tr className="bg-zinc-50 text-zinc-500 text-[11px] uppercase tracking-wider">
                <th className="text-left px-4 py-3">When</th>
                <th className="text-left px-4 py-3">User</th>
                <th className="text-left px-4 py-3">Role</th>
                <th className="text-left px-4 py-3">Method</th>
                <th className="text-left px-4 py-3">Entity</th>
                <th className="text-left px-4 py-3">Path</th>
                <th className="text-right px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Duration</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {items.map((l) => (
                <tr key={l.id} className="hover:bg-zinc-50">
                  <td className="px-4 py-2.5 font-mono text-xs text-zinc-600">{formatDateTime(l.createdAt)}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">{l.userEmail || '—'}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-zinc-500">{l.userRole || '—'}</td>
                  <td className="px-4 py-2.5"><span className="text-xs font-mono px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-800">{l.method}</span></td>
                  <td className="px-4 py-2.5 font-mono text-xs">{l.entity}</td>
                  <td className="px-4 py-2.5 font-mono text-[11px] text-zinc-600 truncate max-w-md">{l.path}</td>
                  <td className={`px-4 py-2.5 text-right font-mono text-xs ${l.statusCode >= 400 ? 'text-red-600' : 'text-emerald-700'}`}>{l.statusCode}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-xs text-zinc-500">{l.meta?.durationMs ?? '—'}ms</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Pagination page={meta.page} totalPages={meta.totalPages} onChange={setPage} />
      </div>
    </div>
  );
}
